import { API } from "@js/api";
import type { RootState } from "@js/store";
import type {
  AllJobsReportStatistics,
  MoneyFlow,
  RewardRule,
  TokenGrantPlan,
} from "@js/types/admin";
import type { CompanyNode } from "@js/types/common";

import type {
  CustomerInvoiceReportFiltersFormParamsType,
  GrantRewardsListFetchParams,
  MoneyFlowDashboardFetchParams,
  OfferReportFiltersFormParamsType,
  RewardRulesListFetchParams,
  TokenBalanceReportFetchParams,
  WithdrawalsReportFetchParams,
} from "./filters";
import type {
  AddNoteArgs,
  AddNoteResponse,
  AddRewardRuleOrderParams,
  AddRewardRuleOrderResponse,
  AdminRewardRulesQueryArg,
  ApprovedReferredTalentArgs,
  BalanceHoldersSearchParams,
  BalanceHoldersSearchResult,
  BulkSendSmsResult,
  ChangeRewardRuleOrderParams,
  ChangeRewardRuleOrderResponse,
  CompanyNodeWithId,
  CreateTGPPayload,
  CustomerInvoiceReportResponse,
  DeactivateRewardRuleResponse,
  DeleteNoteArgs,
  EditRewardRuleOrderParams,
  EditRewardRuleOrderResponse,
  ExpertContributorApplicationHistoryItem,
  ExpertContributorDetail,
  ExpertContributorInvitationHistoryItem,
  ExpertContributorInvitationSummaryItem,
  ExpertContributorMinimal,
  FreelancerLocationMismatch,
  GetBulkTokenGrants,
  GetCompanyNodesIdAndNameOnlyParams,
  GetCustomerInvoiceReportPaymentHistoryParams,
  GetCustomerInvoiceReportPaymentHistoryResponse,
  GetCustomerUnpaidInvoiceReportParams,
  GetCustomerUnpaidInvoiceReportResponse,
  GetDepositReportParams,
  GetDepositReportReturnType,
  GetDepositReportsParams,
  GetDepositReportsReturnType,
  GetDepositReportTotalReturnType,
  GetExpertContributorApplicationsParams,
  GetExpertContributorApplicationsResponse,
  GetExpertContributorsParams,
  GetExpertContributorsResponse,
  GetNotesArgs,
  GetNotesResponse,
  GetTokenTaxesReportParams,
  GetTokenTaxesReportReturnType,
  GrantRewardResponse,
  HideRewardRuleResponse,
  InitiatePaymentToTalentArgs,
  JobReportQueryArg,
  JobReportResponse,
  JobStatsQueryArg,
  MarkInvoiceAsPaidOffPlatformArgs,
  MarkInvoiceAsResolvedArgs,
  MarkInvoiceAsUnresolvedArgs,
  MarkInvoiceAsWrittenOffArgs,
  MarkMultipleInvoicesAsPaidOffPlatform,
  MarkNetdPaymentAsResolvedArgs,
  NLQParsedFilters,
  OfferReportResponse,
  ReferralReportResponse,
  ReferralsReportArgs,
  RetryWithdrawalTransactionQueryArg,
  RewardRulesResponse,
  SaveTokenBalanceItemArgs,
  ShowRewardRuleResponse,
  TalentsWithJobReportParams,
  TalentsWithJobReportResponse,
  TerminateTGPResponse,
  TGHQueryArg,
  TGHResponse,
  TokenBalanceReportResponse,
  TokenBalanceSummaryResponse,
  TokenTransactionsResponse,
  UpdateHealthStatusArgs,
  UploadBulkTokenGrants,
  UploadBulkTokenGrantsResponse,
} from "./types";
import { parseExpertContributorsParams } from "./utils";

const adminDashboardApi = API.injectEndpoints({
  endpoints: (build) => ({
    hideWithdrawalTransaction: build.mutation<void, number>({
      query: (id) => ({
        url: `/admin_dashboard/${id}/hide_withdrawal_transaction/`,
        method: "POST",
      }),
      invalidatesTags: ["MoneyFlowDashboard"],
    }),

    retryWithdrawalTransaction: build.mutation<
      void,
      RetryWithdrawalTransactionQueryArg
    >({
      query: (queryArgs) => ({
        url: `/admin_dashboard/${queryArgs.withdrawalTransactionId}/retry_withdrawal/`,
        method: "POST",
        data: { invoice_id: queryArgs.invoiceId },
      }),
      invalidatesTags: ["MoneyFlowDashboard"],
    }),

    getTokenTaxesReport: build.query<
      GetTokenTaxesReportReturnType,
      GetTokenTaxesReportParams
    >({
      query: (params) => ({
        url: `/token_vesting_report/`,
        method: "GET",
        params,
      }),
    }),

    getFreelancerLocationMismatch: build.query<
      FreelancerLocationMismatch,
      number
    >({
      query: (freelancerId: number) => ({
        url: `/freelancers/${freelancerId}/location_mismatch/`,
        method: "GET",
      }),
    }),

    getBalanceHoldersSearch: build.query<
      BalanceHoldersSearchResult[],
      BalanceHoldersSearchParams
    >({
      query: (params) => ({
        url: `/balanceholders/`,
        method: "GET",
        params: {
          limit: 100,
          ...params,
        },
      }),
    }),
    getBalanceHolder: build.query<BalanceHoldersSearchResult, number>({
      query: (id) => ({
        url: `/balanceholders/${id}/`,
        method: "GET",
      }),
    }),

    getCompanyNodesIdAndNameOnly: build.query<
      CompanyNodeWithId[],
      GetCompanyNodesIdAndNameOnlyParams | void
    >({
      query: (params) => ({
        url: `/company_nodes/return_id_and_name_only/`,
        method: "GET",
        params,
      }),
    }),

    getCompanyNodes: build.query<CompanyNode[], void>({
      query: () => ({
        url: `/company_nodes`,
        method: "GET",
      }),
    }),

    getTokenGrantPlans: build.query<TokenGrantPlan[], void>({
      query: () => {
        return {
          url: "/token_grant_plans/",
          method: "GET",
        };
      },
      providesTags: ["TokenGrantPlans"],
    }),

    createTokenGrantPlan: build.mutation<TokenGrantPlan, CreateTGPPayload>({
      query: (payload) => {
        return {
          url: "/token_grant_plans/",
          method: "POST",
          data: payload,
        };
      },
      invalidatesTags: ["TokenGrantPlans"],
    }),

    terminateTokenGrantPlan: build.mutation<TerminateTGPResponse, number>({
      query: (id) => {
        return {
          url: `/token_grant_plans/${id}/terminate_plan/`,
          method: "POST",
        };
      },
      invalidatesTags: ["TokenGrantPlans"],
    }),

    getDepositReports: build.query<
      GetDepositReportsReturnType,
      GetDepositReportsParams
    >({
      query: (params) => {
        return {
          url: `/deposit_report/`,
          method: "GET",
          params,
        };
      },
    }),

    getDepositReportTotal: build.query<GetDepositReportTotalReturnType, void>({
      query: () => {
        return {
          url: `/deposit_report/total`,
          method: "GET",
        };
      },
    }),

    getDepositReportPerClient: build.query<
      GetDepositReportReturnType,
      GetDepositReportParams
    >({
      query: ({ employer, ordering }) => {
        return {
          url: `/deposit_report/${employer}`,
          method: "GET",
          params: ordering ? { ordering } : undefined,
        };
      },
    }),

    getTokenGrantsHistory: build.query<TGHResponse, TGHQueryArg>({
      query: (queryStringParams) => {
        return {
          url: "/token_grants/",
          params: queryStringParams,
          method: "GET",
        };
      },
    }),

    getJobReportStats: build.query<AllJobsReportStatistics, JobStatsQueryArg>({
      query: (queryStringParams) => {
        return {
          url: "/all_jobs_counts/",
          params: queryStringParams,
          method: "GET",
        };
      },
    }),

    getJobReport: build.query<JobReportResponse, JobReportQueryArg>({
      query: (queryStringParams) => {
        return {
          url: "/all_jobs_report/",
          method: "GET",
          params: queryStringParams,
        };
      },
    }),
    getMoneyFlowDashboard: build.query<
      MoneyFlow,
      Partial<MoneyFlowDashboardFetchParams>
    >({
      query: (queryStringParams) => {
        return {
          url: "/admin_dashboard/",
          method: "GET",
          params: queryStringParams,
        };
      },
      providesTags: ["MoneyFlowDashboard"],
    }),
    getOfferReport: build.query<
      OfferReportResponse,
      Partial<OfferReportFiltersFormParamsType>
    >({
      query: (queryStringParams) => {
        return {
          url: "/offer_report/",
          method: "GET",
          params: queryStringParams,
        };
      },
      providesTags: ["OfferReport"],
    }),

    getRewardRules: build.query<
      RewardRulesResponse,
      Partial<RewardRulesListFetchParams>
    >({
      query: (params) => {
        return {
          url: "/manage_reward_rules/",
          method: "GET",
          params,
        };
      },
      providesTags: ["RewardRules"],
    }),

    deactivateRewardRule: build.mutation<DeactivateRewardRuleResponse, number>({
      query: (id) => {
        return {
          url: `/manage_reward_rules/${id}/deactivate/`,
          method: "POST",
        };
      },
      invalidatesTags: ["RewardRules"],
    }),

    hideRewardRule: build.mutation<HideRewardRuleResponse, number>({
      query: (id) => {
        return {
          url: `/manage_reward_rules/${id}/hide/`,
          method: "POST",
        };
      },
      invalidatesTags: ["RewardRules"],
    }),
    showRewardRule: build.mutation<ShowRewardRuleResponse, number>({
      query: (id) => {
        return {
          url: `/manage_reward_rules/${id}/show/`,
          method: "POST",
        };
      },
      invalidatesTags: ["RewardRules"],
    }),
    changeRewardRuleOrder: build.mutation<
      ChangeRewardRuleOrderResponse,
      ChangeRewardRuleOrderParams
    >({
      query: ({ id, order }) => {
        return {
          url: `/manage_reward_rules/${id}/change_order/`,
          method: "POST",
          data: { order },
        };
      },
      invalidatesTags: ["RewardRules"],
    }),
    editRewardRule: build.mutation<
      EditRewardRuleOrderResponse,
      EditRewardRuleOrderParams
    >({
      query: (values) => {
        return {
          url: `/manage_reward_rules/${values.id}`,
          method: "PATCH",
          data: values,
        };
      },
      invalidatesTags: ["RewardRules"],
    }),
    addRewardRule: build.mutation<
      AddRewardRuleOrderResponse,
      AddRewardRuleOrderParams
    >({
      query: (values) => {
        return {
          url: `/manage_reward_rules/`,
          method: "POST",
          data: values,
        };
      },
      invalidatesTags: ["RewardRules"],
    }),
    getReferralReport: build.query<ReferralReportResponse, ReferralsReportArgs>(
      {
        query: (params) => {
          return {
            url: "/referral_report/",
            method: "GET",
            params,
          };
        },
        providesTags: ["ReferralReport"],
      },
    ),
    approveReferredTalent: build.mutation<null, ApprovedReferredTalentArgs>({
      query: (talentId) => {
        return {
          url: `/talent/${talentId}/approve/`,
          method: "POST",
        };
      },
      invalidatesTags: ["ReferralReport"],
    }),
    getGrantRewards: build.query<
      GrantRewardResponse,
      Partial<GrantRewardsListFetchParams>
    >({
      query: (params) => {
        return {
          url: "/manage_rewards/",
          method: "GET",
          params,
        };
      },
    }),
    getTalentsWithJobsReport: build.query<
      TalentsWithJobReportResponse,
      TalentsWithJobReportParams
    >({
      query: (params) => {
        return {
          url: "/talents_with_jobs_report/",
          method: "GET",
          params,
        };
      },
      providesTags: ["TalentsWithJobsReport"],
    }),
    updateHealthStatus: build.mutation<null, UpdateHealthStatusArgs>({
      query: ({ id, health_status }) => {
        return {
          url: `/talents_with_jobs_report/${id}/update_health_status/`,
          method: "POST",
          data: {
            health_status,
          },
        };
      },
      invalidatesTags: ["TalentsWithJobsReport"],
    }),
    getAdminRewardRules: build.query<RewardRule[], AdminRewardRulesQueryArg>({
      query: (params) => {
        return {
          url: "/reward_rules/",
          method: "GET",
          params,
        };
      },
    }),

    getCustomerInvoiceReport: build.query<
      CustomerInvoiceReportResponse,
      CustomerInvoiceReportFiltersFormParamsType
    >({
      query: (params) => {
        return {
          url: "/invoice_report/",
          method: "GET",
          params,
        };
      },
      providesTags: ["InvoiceReport"],
    }),

    getCustomerUnpaidInvoiceReport: build.query<
      GetCustomerUnpaidInvoiceReportResponse,
      GetCustomerUnpaidInvoiceReportParams
    >({
      query: (params) => {
        return {
          url: "/invoice_report/unpaid_invoices",
          method: "GET",
          params,
        };
      },
      providesTags: ["InvoiceReportUnpaidInvoices"],
    }),

    getCustomerInvoiceReportPaymentHistory: build.query<
      GetCustomerInvoiceReportPaymentHistoryResponse,
      GetCustomerInvoiceReportPaymentHistoryParams
    >({
      query: ({ invoiceId }) => {
        return {
          url: `/invoice_report/${invoiceId}/settle_payment_history`,
          method: "GET",
        };
      },
      providesTags: ["InvoiceReportPaymentHistory"],
    }),

    initiatePaymentToTalent: build.mutation<null, InitiatePaymentToTalentArgs>({
      query: ({ invoiceId, amount }) => {
        return {
          url: `/invoice_report/${invoiceId}/pay_talent_by_netd/`,
          method: "POST",
          data: { amount },
        };
      },
      invalidatesTags: ["InvoiceReport"],
    }),

    markNetdPaymentAsResolved: build.mutation<
      null,
      MarkNetdPaymentAsResolvedArgs
    >({
      query: (invoiceId) => {
        return {
          url: `/invoice_report/${invoiceId}/mark_netd_as_resolved/`,
          method: "POST",
        };
      },
      invalidatesTags: ["InvoiceReport"],
    }),

    markInvoiceAsResolved: build.mutation<null, MarkInvoiceAsResolvedArgs>({
      query: (invoiceId) => {
        return {
          url: `/invoice_report/${invoiceId}/mark_invoice_as_paid_do_not_initiate_payment/`,
          method: "POST",
        };
      },
      invalidatesTags: ["InvoiceReport"],
    }),

    markInvoiceAsUnresolved: build.mutation<null, MarkInvoiceAsUnresolvedArgs>({
      query: (invoiceId) => {
        return {
          url: `/invoice_report/${invoiceId}/mark_netd_invoice_as_unresolved/`,
          method: "POST",
        };
      },
      invalidatesTags: ["InvoiceReport"],
    }),

    markInvoiceAsWrittenOff: build.mutation<null, MarkInvoiceAsWrittenOffArgs>({
      query: (invoiceId) => {
        return {
          url: `/invoice_report/${invoiceId}/mark_as_written_off/`,
          method: "POST",
        };
      },
      invalidatesTags: ["InvoiceReport"],
    }),

    markInvoiceAsPaidOffPlatform: build.mutation<
      null,
      MarkInvoiceAsPaidOffPlatformArgs
    >({
      query: ({ invoiceId, amount }) => {
        return {
          url: `/invoice_report/${invoiceId}/mark_as_paid_off_platform/`,
          method: "POST",
          data: { amount },
        };
      },
      invalidatesTags: ["InvoiceReport"],
    }),
    markMultipleInvoicesAsPaidOffPlatform: build.mutation<
      null,
      MarkMultipleInvoicesAsPaidOffPlatform
    >({
      query: (data) => {
        return {
          url: `/invoice_report/settle_employer_payments/`,
          method: "POST",
          data,
        };
      },
      invalidatesTags: [
        "InvoiceReport",
        "InvoiceReportUnpaidInvoices",
        "InvoiceReportPaymentHistory",
      ],
    }),
    getTokenBalanceReport: build.query<
      TokenBalanceReportResponse,
      TokenBalanceReportFetchParams
    >({
      query: (params) => {
        return {
          url: "/token_balance_report_items/",
          method: "GET",
          params,
        };
      },
    }),
    getTokenBalanceReportSummary: build.query<
      TokenBalanceSummaryResponse,
      void
    >({
      query: () => {
        return {
          url: "/token_balance_report_summary/",
          method: "GET",
        };
      },
    }),
    getOfferNotes: build.query<GetNotesResponse, GetNotesArgs>({
      query: (params) => {
        return {
          url: "/offer_notes/",
          method: "GET",
          params,
        };
      },
      providesTags: ["OfferNotes"],
    }),
    addOfferNote: build.mutation<AddNoteResponse, AddNoteArgs>({
      query: (args) => {
        return {
          url: `/offer_notes/`,
          method: "POST",
          data: args,
        };
      },
      invalidatesTags: ["OfferNotes", "OfferReport"],
    }),
    deleteOfferNote: build.mutation<void, DeleteNoteArgs>({
      query: ({ id }) => {
        return {
          url: `/offer_notes/${id}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["OfferNotes", "OfferReport"],
    }),
    getNotes: build.query<GetNotesResponse, GetNotesArgs>({
      query: (params) => {
        return {
          url: "/notes/",
          method: "GET",
          params,
        };
      },
      providesTags: (_res, _err, arg) => [
        { type: "Notes", noteType: arg.target_name },
      ],
    }),
    addNote: build.mutation<AddNoteResponse, AddNoteArgs>({
      query: (args) => {
        return {
          url: `/notes/`,
          method: "POST",
          data: args,
        };
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: "Notes", noteType: arg.target.object_name },
      ],
    }),
    deleteNote: build.mutation<void, DeleteNoteArgs>({
      query: ({ id }) => {
        return {
          url: `/notes/${id}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: "Notes", noteType: arg.target_name },
      ],
    }),
    getBulkTokenGrants: build.query<GetBulkTokenGrants, { id: number }>({
      query: ({ id }) => {
        return {
          url: `/bulk_token_grants/${id}`,
          method: "GET",
        };
      },
    }),
    uploadBulkTokenGrants: build.mutation<
      UploadBulkTokenGrantsResponse,
      UploadBulkTokenGrants
    >({
      query: ({ id, ...data }) => {
        return {
          url: `/bulk_token_grants/${id}`,
          method: "PUT",
          data: { confirmation: true, ...data },
        };
      },
    }),
    saveTokenBalanceItem: build.mutation<
      { key: string },
      SaveTokenBalanceItemArgs
    >({
      query: (args) => {
        const values = args;

        if (!values.debit) {
          // To avoid "This field may not be null." error.
          delete values.debit;
        }

        if (!values.credit) {
          // To avoid "This field may not be null." error.
          delete values.credit;
        }

        if (values.code) {
          // When 2FA code is required and we send new code "key" shouldn't be sent (so it won't be validated).
          delete values.key;
        }

        return {
          url: `/token_balance_items/`,
          method: "POST",
          data: values,
        };
      },
      invalidatesTags: ["WalletBalance"],
    }),
    getTokenTransactions: build.query<
      TokenTransactionsResponse,
      WithdrawalsReportFetchParams
    >({
      query: (params) => {
        return {
          url: "/token_transactions/",
          method: "GET",
          params,
        };
      },
      providesTags: ["TokenTransactionsList"],
    }),
    markWithdrawalAsCompleted: build.mutation<void, number>({
      query: (withdrawalTransactionId) => {
        return {
          url: `/token_transactions/${withdrawalTransactionId}/mark_as_completed/`,
          method: "POST",
        };
      },
      invalidatesTags: ["TokenTransactionsList"],
    }),
    getExpertContributorsMinimal: build.query<
      ExpertContributorMinimal[],
      { ids: number[] }
    >({
      query: (params) => {
        return {
          url: "/expert_contributors_minimal/",
          method: "GET",
          params: { id: params.ids.join(",") },
        };
      },
    }),
    getExpertContributors: build.query<
      GetExpertContributorsResponse,
      GetExpertContributorsParams
    >({
      query: (params) => {
        const parsedParams = parseExpertContributorsParams(params);
        return {
          url: "/expert_contributors/",
          method: "GET",
          params: parsedParams,
        };
      },
      providesTags: ["ExpertContributors"],
    }),
    getExpertContributor: build.query<ExpertContributorDetail, number>({
      query: (id) => ({
        url: `/expert_contributors/${id}/`,
        method: "GET",
      }),
      providesTags: (_res, _err, arg) => [
        { type: "ExpertContributors", id: arg },
      ],
    }),
    getExpertContributorApplications: build.query<
      GetExpertContributorApplicationsResponse,
      GetExpertContributorApplicationsParams
    >({
      query: ({ id, ...params }) => ({
        url: `/expert_contributors/${id}/applications/`,
        method: "GET",
        params,
      }),
      providesTags: ["ExpertContributorsApplications"],
    }),
    updateExpertContributorSentToClient: build.mutation<
      null,
      { id: number; sent_to_client: boolean }
    >({
      query: ({ id, sent_to_client }) => ({
        url: `/expert_contributors/${id}/update_sent_to_client/`,
        method: "PATCH",
        data: { sent_to_client },
      }),
      async onQueryStarted(
        { id, sent_to_client },
        { dispatch, getState, queryFulfilled },
      ) {
        const state = getState() as RootState;
        const patchResults: Array<{ undo: () => void }> = [];
        const cachedArgs = adminDashboardApi.util.selectCachedArgsForQuery(
          state,
          "getExpertContributors",
        );

        for (const args of cachedArgs) {
          const patchResult = dispatch(
            adminDashboardApi.util.updateQueryData(
              "getExpertContributors",
              args,
              (draft) => {
                const expert = draft.results.find(
                  (contributor) => contributor.id === id,
                );
                if (!expert) {
                  return;
                }
                expert.sent_to_client = sent_to_client;
              },
            ),
          );
          patchResults.push(patchResult);
        }

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patch) => patch.undo());
        }
      },
    }),
    parseNlq: build.query<NLQParsedFilters, string>({
      query: (prompt) => ({
        url: "/expert_contributors/parse_nlq/",
        method: "GET",
        params: { prompt },
      }),
    }),
    expertContributorBulkSendSms: build.mutation<
      BulkSendSmsResult[],
      { message: string; freelancer_ids: number[] }
    >({
      query: ({ message, freelancer_ids }) => ({
        url: "/expert_contributors/bulk_send_sms/",
        method: "POST",
        data: { message, freelancer_ids },
      }),
    }),
    getInvitationHistory: build.query<
      ExpertContributorInvitationHistoryItem[],
      { freelancerIds: number[]; limit?: number }
    >({
      query: ({ freelancerIds, limit }) => ({
        url: "/expert_contributors/invitation_history/",
        method: "GET",
        params: { freelancer_ids: freelancerIds.join(","), limit },
      }),
      providesTags: ["ExpertContributorsInvitations"],
    }),
    getApplicationHistory: build.query<
      ExpertContributorApplicationHistoryItem[],
      { freelancerIds: number[]; limit?: number }
    >({
      query: ({ freelancerIds, limit }) => ({
        url: "/expert_contributors/application_history/",
        method: "GET",
        params: { freelancer_ids: freelancerIds.join(","), limit },
      }),
      providesTags: ["ExpertContributorsApplications"],
    }),
    getInvitationSummary: build.query<
      ExpertContributorInvitationSummaryItem[],
      number[]
    >({
      query: (freelancerIds) => ({
        url: "/expert_contributors/invitation_summary/",
        method: "GET",
        params: { freelancer_ids: freelancerIds.join(",") },
      }),
      providesTags: ["ExpertContributorsInvitations"],
    }),
  }),
});

export const {
  useRetryWithdrawalTransactionMutation,
  useGetMoneyFlowDashboardQuery,
  useHideWithdrawalTransactionMutation,
  useGetTokenTaxesReportQuery,
  useLazyGetTokenTaxesReportQuery,
  useGetFreelancerLocationMismatchQuery,
  useGetCompanyNodesIdAndNameOnlyQuery,
  useGetCompanyNodesQuery,
  useGetTokenGrantsHistoryQuery,
  useGetTokenGrantPlansQuery,
  useCreateTokenGrantPlanMutation,
  useTerminateTokenGrantPlanMutation,
  useGetDepositReportsQuery,
  useGetDepositReportTotalQuery,
  useGetDepositReportPerClientQuery,
  useGetJobReportStatsQuery,
  useGetJobReportQuery,
  useGetOfferReportQuery,
  useGetRewardRulesQuery,
  useDeactivateRewardRuleMutation,
  useHideRewardRuleMutation,
  useShowRewardRuleMutation,
  useChangeRewardRuleOrderMutation,
  useEditRewardRuleMutation,
  useAddRewardRuleMutation,
  useGetReferralReportQuery,
  useApproveReferredTalentMutation,
  useGetGrantRewardsQuery,
  useGetAdminRewardRulesQuery,
  useGetTalentsWithJobsReportQuery,
  useUpdateHealthStatusMutation,
  useGetOfferNotesQuery,
  useAddOfferNoteMutation,
  useDeleteOfferNoteMutation,
  useGetNotesQuery,
  useAddNoteMutation,
  useDeleteNoteMutation,
  useGetCustomerInvoiceReportQuery,
  useInitiatePaymentToTalentMutation,
  useMarkInvoiceAsResolvedMutation,
  useMarkNetdPaymentAsResolvedMutation,
  useMarkInvoiceAsUnresolvedMutation,
  useMarkInvoiceAsWrittenOffMutation,
  useMarkInvoiceAsPaidOffPlatformMutation,
  useMarkMultipleInvoicesAsPaidOffPlatformMutation,
  useGetTokenBalanceReportQuery,
  useGetTokenBalanceReportSummaryQuery,
  useSaveTokenBalanceItemMutation,
  useGetBalanceHoldersSearchQuery,
  useGetBalanceHolderQuery,
  useGetTokenTransactionsQuery,
  useMarkWithdrawalAsCompletedMutation,
  useGetCustomerUnpaidInvoiceReportQuery,
  useGetCustomerInvoiceReportPaymentHistoryQuery,
  useGetBulkTokenGrantsQuery,
  useUploadBulkTokenGrantsMutation,
  useGetExpertContributorsQuery,
  useGetExpertContributorQuery,
  useGetExpertContributorApplicationsQuery,
  useGetExpertContributorsMinimalQuery,
  useLazyGetExpertContributorsMinimalQuery,
  useUpdateExpertContributorSentToClientMutation,
  useLazyParseNlqQuery,
  useExpertContributorBulkSendSmsMutation,
  useGetInvitationHistoryQuery,
  useGetApplicationHistoryQuery,
  useGetInvitationSummaryQuery,
} = adminDashboardApi;

export type { JobReportResponse, TerminateTGPResponse, TGHResponse };
